/**
 * Project: academy-v2
 * File: Index
 * Author: wanpeninsula
 * Author URI: https://www.pennycodes.dev
 * Created: 25/01/2024 at 7:25 pm
 *
 * Copyright (c) 2024 academy-v2. All rights reserved.
 */
import CalendarHeading from "@/components/agenda/mobile/CalendarHeading.tsx";
import Heading from "@/components/agenda/mobile/Heading.tsx";
import Slot from "@/components/agenda/mobile/Slot.tsx";
import routes from "@/config/routes.ts";
import { useMore } from "@/contexts/MoreContext";
import { useMe, useUserEventsAndMaterials } from "@/data/user.ts";
import DashboardContentLayout from "@/layouts/DashboardContentLayout.tsx";

import {
  FEEDBACK_EVENT_ID,
  INTERNAL_DATE_FORMAT,
  getDateBetween,
  getTitle,
  scrollToday,
  updateCalenderAndHeader,
} from "@/utils/agenda.ts";
import { debounce } from "@/utils/debounce.ts";
import { uuid } from "@/utils/string.ts";
import ContentLoader from "@edusogno/composites/content-loader";
import Button from "@edusogno/primitives/button.js";
import cn from "classnames";
import dayjs from "dayjs";
// import Button from "@edusogno/primitives/button.js";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { t } from "i18next";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

const endDate = dayjs().add(2, "month");

const MobileAgendaView = () => {
  // const navigate = useNavigate();

  const { eventMaterials } = useUserEventsAndMaterials();
  const { me } = useMe();
  const [deferLoading, setDeferLoading] = useState(true);
  const signUpDate = me?.item?.user?.created_at
    ? dayjs(me?.item?.user?.created_at).startOf("month")
    : dayjs();

  const dates = useMemo(
    () => getDateBetween(signUpDate, endDate),
    [signUpDate],
  );

  const { isBeta } = useMore();

  //ref to remember previous scroll position to check whether scrolling up or down
  const scrollDirection = useRef(-1);

  ///fix for safari render issue disable today button until scroll end
  const handleEndScroll = useMemo(
    () =>
      debounce(() => {
        const todayButton = document.querySelector(`#todayButton`);
        todayButton?.removeAttribute("disabled");
      }, 500),
    [],
  );
  //Center the current day  when all dates are loaded
  const navigate = useNavigate();
  useEffect(() => {
    scrollToday();
  }, [dates]);

  useEffect(() => {
    if (!eventMaterials || !eventMaterials.item.events) return;
    const events = eventMaterials.item.events;
    const idEventForFeedback = localStorage.getItem(FEEDBACK_EVENT_ID);

    if (idEventForFeedback && events.length) {
      const idEvent = parseInt(idEventForFeedback);

      const event = events.find((e) => e.id === idEvent);

      if (!event) {
        localStorage.removeItem(FEEDBACK_EVENT_ID);
        return;
      }

      const title = getTitle(event);

      const start = new Date(`${event?.data} ${event?.ora_fine}`);
      let modifiedType = event.type;

      if (
        event.type.toLowerCase() === "speaking" &&
        event.descrizione === "Mock test"
      ) {
        modifiedType = "MockTest";
      }

      if (start <= new Date()) {
        navigate(routes.agenda_feedback, {
          state: {
            idEvent,
            date: start,
            tutor_nome: event?.tutors?.first_name,
            title,
            type: modifiedType,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventMaterials]);

  useEffect(() => {
    const id = setTimeout(() => setDeferLoading(false), 1000);
    return () => clearTimeout(id);
  }, []);

  const handleHeading = () => {
    //Change color of today button when the list is scrolled away
    const container = document.querySelector(`#containerBox`) as HTMLElement;
    const todayElement = document.querySelector(
      `[data-todaytarget="true"]`,
    ) as HTMLElement;
    const todayButton = document.querySelector(`#todayButton`) as HTMLElement;
    todayButton?.setAttribute("disabled", "true");

    const DayDiff = Math.floor(todayElement.offsetTop - container.scrollTop);
    if (DayDiff <= 340 && DayDiff > 50) {
      todayButton?.setAttribute("data-alt", "true");
      todayButton?.setAttribute("disabled", "true");
    } else {
      if (todayButton?.getAttribute("data-alt") !== "false") {
        todayButton?.setAttribute("data-alt", "false");
        todayButton?.removeAttribute("disabled");
      }
    }

    //Find heading closest to top and update header and min calendar
    const headings = document.querySelectorAll(`[data-heading]`);

    if (headings && container) {
      for (const heading of headings) {
        const diff = Math.floor(
          (heading as HTMLElement)?.offsetTop - container?.scrollTop,
        );
        const attribute = heading.getAttribute("data-date");
        if (scrollDirection.current > container.scrollTop) {
          if (diff < 100) updateCalenderAndHeader(attribute);
        } else {
          if (diff < 30 && diff > 0) updateCalenderAndHeader(attribute);
        }
      }
      scrollDirection.current = container.scrollTop;
    }
    handleEndScroll();
  };
  if (deferLoading) return <ContentLoader />;

  return (
    <DashboardContentLayout className={"relative px-0 !overflow-hidden"}>
      <div className="flex flex-col items-center px-4">
        {/* schedule speaking class */}
        {isBeta && (
          <Button
            variant={"contained"}
            color={"primary"}
            className={cn("w-full mt-3")}
            size={"block"}
            onClick={() => {
              navigate("book-speaking-class", { relative: true } as never);
            }}
          >
            {t("btns.speaking_class")}
          </Button>
        )}
        <div
          className={
            " h-fit flex flex-row items-end py-4 bg-brand-background  !overflow-hidden w-full  md:px-9 z-50 md:!pl-0"
          }
        >
          <div id={"observerBox"} className="block w-full [&>button]:flex">
            <CalendarHeading events={eventMaterials?.item?.events || []} />
          </div>
        </div>
      </div>

      <div
        id={"containerBox"}
        onScroll={() => handleHeading()}
        className={cn(
          "text-white w-full  gap-6 overflow-y-auto  custom-scroll-bug-fix shadow-event px-3",
        )}
      >
        {dates.map((value) => {
          const Item = dayjs(value as string, INTERNAL_DATE_FORMAT);
          return (
            <Fragment key={uuid()}>
              {Item.date() === 1 && (
                <Heading
                  data-date={Item.format(INTERNAL_DATE_FORMAT)}
                  data-heading={Item.format(INTERNAL_DATE_FORMAT)}
                  date={Item}
                />
              )}
              <>
                <Slot
                  data-todaytarget={`${Item.isSame(dayjs(), "day")}`}
                  data-dateitem={`${Item.format(INTERNAL_DATE_FORMAT)}`}
                  data-first={`${Item.date() === 1}`}
                  date={Item}
                  events={(eventMaterials?.item?.events || []).filter((event) =>
                    dayjs(event.data).isSame(Item, "day"),
                  )}
                />
              </>
            </Fragment>
          );
        })}
      </div>
    </DashboardContentLayout>
  );
};

export default MobileAgendaView;
